import React, { useState } from "react";
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Api from "../services/apis/setup";

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotPassword] = Api.useForgotPasswordMutation();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response: any = await forgotPassword({
        baseURL: "/auth/forgot-password",
        payload: JSON.stringify({ email }),
      });

      if (response?.data?.success) {
        setSubmitted(true);
      } else {
        setError(response?.data?.message || "Failed to send reset email");
      }
    } catch (err: any) {
      setError(err?.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f5f5",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 450,
          width: "100%",
          borderRadius: 2,
        }}
      >
        {submitted ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" gutterBottom sx={{ color: "success.main", fontWeight: 600 }}>
              Check Your Email
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We have sent password reset instructions to <strong>{email}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If you don't see the email, check your spam folder or try again.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/login")}
              sx={{ backgroundColor: "var(--primary-color)" }}
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Forgot Password?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your email address and we'll send you instructions to reset your password.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <EmailIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: "var(--primary-color)",
                  py: 1.5,
                  mb: 2,
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Send Reset Link"}
              </Button>
            </form>

            <Box sx={{ textAlign: "center" }}>
              <Button
                component={RouterLink}
                to="/login"
                startIcon={<ArrowBackIcon />}
                sx={{ textTransform: "none" }}
              >
                Back to Login
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
